/**
 * AI Support Chat Component
 * 
 * Custom chat component for AI chatbot interactions
 * Uses custom message hook and displays messages in chat format
 */

import React, { useCallback } from 'react';
import { Box, Typography, CircularProgress, Alert } from '@mui/material';
import { useRecoilValue } from 'recoil';
import firebase from 'firebase/compat/app';
import VirtualizedMessageList from '../../molecules/chat/VirtualizedMessageList';
import InputToolbar, { InputToolbarMethods } from '../../molecules/chat/InputToolbar';
import { useAISupportMessages } from '../../../hooks/useAISupportMessages';
import userDataState from '../../../atoms/userDataAtom';
import { useRef } from 'react';

const AI_BOT_GROUP_ID = 'm45-bot-support';
const AI_BOT_NAME = 'M45 Bot';

interface AISupportChatProps {
  onHeaderUpdate?: (title: string) => void;
}

const AISupportChat: React.FC<AISupportChatProps> = ({ onHeaderUpdate }) => {
  // Use Firebase Auth UID instead of localStorage to match Firestore security rules
  const userId = firebase.auth().currentUser?.uid || '';
  const userData = useRecoilValue(userDataState) as any;
  const inputToolbarRef = useRef<InputToolbarMethods>(null);

  const { messages, loading, sendMessage, error } = useAISupportMessages({
    userId,
  });

  // Convert AI messages to chat message format
  const chatMessages = messages.map((msg) => ({
    id: msg.id,
    fromUID: msg.role === 'user' ? userId : AI_BOT_GROUP_ID,
    fromUser: {
      uid: msg.role === 'user' ? userId : AI_BOT_GROUP_ID,
      firstName: msg.role === 'user' ? (userData?.firstName || 'You') : 'M45 Bot',
      lastName: msg.role === 'user' ? (userData?.lastName || '') : '',
    },
    message: msg.content,
    createdOn: msg.timestamp,
    messageType: 'text' as const,
    groupId: AI_BOT_GROUP_ID,
    sources: msg.sources,
  }));

  // Update header
  React.useEffect(() => {
    if (onHeaderUpdate) {
      const title = chatMessages.length > 0 
        ? `${AI_BOT_NAME} (${chatMessages.length} messages)`
        : AI_BOT_NAME;
      onHeaderUpdate(title);
    }
  }, [onHeaderUpdate, chatMessages.length]);

  // Handle send message
  const handleSendText = useCallback(async (text: string) => {
    await sendMessage(text);
  }, [sendMessage]);

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#ffffff',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Chat Header */}
      <Box
        sx={{
          borderBottom: '1px solid rgba(0, 119, 200, 0.1)',
          backgroundColor: '#ffffff',
          px: 3,
          py: 1.5,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a2d66', m: 0 }}>
          {AI_BOT_NAME}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
          <Typography variant="caption" sx={{ color: '#6B7280' }}>
            {chatMessages.length} message{chatMessages.length !== 1 ? 's' : ''}
          </Typography>
          {loading && (
            <Typography variant="caption" sx={{ color: '#2563EB' }}>
              • AI is thinking...
            </Typography>
          )}
        </Box>
      </Box>

      {/* Message List */}
      <VirtualizedMessageList
        messages={chatMessages}
        currentUserId={userId}
        currentUserType={userData?.type || userData?.userType || 'client'}
        onLoadMore={() => {}} // No pagination for AI chat
        onMessageLongPress={() => {}}
        onImagePress={() => {}}
        onReactionPress={() => {}} // Reactions not supported for AI chat
        onReplyPress={() => {}} // Replies not supported for AI chat
        onDeletePress={() => {}} // Deletion not supported for AI chat
        loading={false}
        hasMore={false}
      />

      {/* Input Toolbar */}
      <InputToolbar
        ref={inputToolbarRef}
        onSendText={handleSendText}
        onStartRecording={() => {}} // Audio not supported for AI chat
        onAttachmentPress={() => {}} // Attachments not supported for AI chat
        onSelectImage={() => {}}
        onSelectVideo={() => {}}
        onSelectDocument={() => {}}
        placeholder="Ask M45 Bot anything..."
        groupId={AI_BOT_GROUP_ID}
        hideAudioControls={true} // Hide audio recording controls for AI bot
        hideAttachmentControls={true} // Hide attachment controls for AI bot
      />

      {/* Loading indicator */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 80,
            right: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: 1,
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <CircularProgress size={16} />
          <Typography variant="caption">AI is thinking...</Typography>
        </Box>
      )}
    </Box>
  );
};

export default AISupportChat;
